import React, { useContext } from "react";
import { Tag } from "antd";
import dayjs from "dayjs";
import { EvidenceRef, EvidenceSource } from "../../../types/healthcare/PatientAnalysisTypes";
import { EvidenceRefInteractionContext } from "./EvidenceRefInteractionContext";

/**
 * Shared evidence-ref display, used by the comprehensive analysis and by the
 * AI-filled treatment-plan form fields. Renders a compact list of references to
 * the original source data (genetics, blood, history, …) the way the analysis does.
 *
 * Kept intentionally small with a clean `refs` props interface so later subtasks
 * can layer on date formatting and click-to-open behaviour with minimal change.
 */

export const SOURCE_LABEL: Record<string, string> = {
	genetic_panel:   "Genetics",
	blood_results:   "Blood",
	lab_results:     "Lab",
	medical_history: "History",
	medications:     "Meds",
	diet_scores:     "Diet",
	exercise_scores: "Exercise",
	questionnaire:   "Survey",
};

export function formatRef(ref: EvidenceRef): string {
	switch (ref.source) {
		case "genetic_panel": {
			const parts = [ref.gene];
			if (ref.variant) parts.push(ref.variant);
			if (ref.genotype) parts.push(`(${ref.genotype})`);
			return parts.filter(Boolean).join(" ") || "Genetic Panel";
		}
		case "blood_results":
		case "lab_results": {
			if (ref.test_name && ref.value) return `${ref.test_name}: ${ref.value}`;
			if (ref.test_name) return ref.test_name;
			return ref.source === "blood_results" ? "Blood Results" : "Lab Results";
		}
		case "medical_history": {
			return ref.condition || ref.surgery || ref.medication || "Medical History";
		}
		case "medications": {
			return ref.medication || "Medications";
		}
		case "diet_scores": {
			if (ref.field && ref.value_seen) return `${ref.field}: ${ref.value_seen}`;
			return "Dietary Assessment";
		}
		case "exercise_scores": {
			if (ref.field && ref.value_seen) return `${ref.field}: ${ref.value_seen}`;
			return "Exercise Assessment";
		}
		case "questionnaire": {
			if (ref.field && ref.value_seen) return `${ref.field}: ${ref.value_seen}`;
			return "Questionnaire";
		}
		default:
			return ref.source;
	}
}

/**
 * Format the date the referenced record was performed/captured (e.g. the date a
 * blood test was taken, or a medical-history entry recorded) for display on hover.
 */
function formatRefDate(raw: string): string {
	const d = dayjs(raw);
	return d.isValid() ? d.format("D MMM YYYY") : raw;
}

export interface EvidenceRefListProps {
	refs: EvidenceRef[];
}

/**
 * Sources whose original record has a detail modal we can open. Soft/aggregate
 * sources (diet/exercise/questionnaire) have no single record to open and stay
 * non-interactive.
 */
const CLICKABLE_SOURCES: ReadonlySet<EvidenceSource> = new Set<EvidenceSource>([
	"blood_results",
	"lab_results",
	"medical_history",
	"medications",
	"genetic_panel",
]);

export const EvidenceRefList: React.FC<EvidenceRefListProps> = ({ refs }) => {
	const { onRefClick } = useContext(EvidenceRefInteractionContext);
	if (!refs || refs.length === 0) return null;
	return (
		<div style={{ maxWidth: 320 }}>
			{refs.map((ref, i) => {
				// A ref is interactive only when a handler is provided AND its source has a
				// detail modal. Without a provider this is exactly the original behaviour.
				const isClickable = !!onRefClick && CLICKABLE_SOURCES.has(ref.source);
				const rowStyle: React.CSSProperties = {
					marginBottom: i < refs.length - 1 ? 6 : 0,
					...(isClickable ? { cursor: "pointer", color: "var(--mg-primary)" } : {}),
				};
				return (
					<div
						key={i}
						style={rowStyle}
						className={isClickable ? "mg-evidence-ref-clickable" : undefined}
						role={isClickable ? "button" : undefined}
						onClick={isClickable ? () => onRefClick!(ref) : undefined}
					>
						<Tag color="blue" style={{ marginRight: 4, fontSize: 11 }}>
							{SOURCE_LABEL[ref.source] ?? ref.source}
						</Tag>
						<span style={{ fontSize: 12, textDecoration: isClickable ? "underline" : "none", textDecorationStyle: "dotted" }}>
							{formatRef(ref)}
						</span>
						{ref.record_date && (
							<span style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginLeft: 6 }}>
								· {formatRefDate(ref.record_date)}
							</span>
						)}
						{ref.note && (
							<div style={{ fontSize: 11, color: "var(--mg-text-secondary)", marginTop: 2 }}>
								{ref.note}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default EvidenceRefList;
